import React from 'react';
import Link from 'next/link';
import styled from 'styled-components';

import Imgix from 'shared/components/imgix';
import { getCategoryPhoto } from 'shared/helpers/dispensaries';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { DispensaryMenuCategory } from 'src/dispensary/hooks/use-dispensary-categories';
import { CategoriesTypeOption, RadiusOption } from 'src/gql/graphql';

type SpecialsCategoryCardProps = {
  category: DispensaryMenuCategory;
  href: string;
  type: CategoriesTypeOption;
  style: RadiusOption;
};

// Matches the radius scale used by the main category nav's bordered image cards (large/compact)
const cardRadiusOptions: Record<RadiusOption, number | string> = {
  [RadiusOption.rounded]: '16px',
  [RadiusOption.roundedRectangle]: '12px',
  [RadiusOption.square]: 0,
};

// Matches the radius scale used by the main category nav's avatar images (stacked) and text pills
const avatarRadiusOptions: Record<RadiusOption, number | string> = {
  [RadiusOption.rounded]: '9999px',
  [RadiusOption.roundedRectangle]: '12px',
  [RadiusOption.square]: 0,
};

export function SpecialsCategoryCard({ category, href, type, style }: SpecialsCategoryCardProps): JSX.Element {
  const { dispensary } = useDispensary();
  const { label, cardImage } = category;

  if (type === CategoriesTypeOption.text) {
    return (
      <TextPillLink href={href} $radius={avatarRadiusOptions[style]} data-testid='specials-category-card'>
        {label}
      </TextPillLink>
    );
  }

  const image = cardImage ?? getCategoryPhoto(dispensary, category);

  if (type === CategoriesTypeOption.stacked) {
    return (
      <StackedLink href={href} data-testid='specials-category-card'>
        <StackedImageWrapper $radius={avatarRadiusOptions[style]}>
          <StackedImage
            src={image}
            disableSrcSet
            imgixParams={{ height: 256 }}
            htmlAttributes={{ alt: `${label} category image` }}
          />
        </StackedImageWrapper>
        <StackedLabel>{label}</StackedLabel>
      </StackedLink>
    );
  }

  if (type === CategoriesTypeOption.compact) {
    return (
      <CompactLink href={href} $radius={cardRadiusOptions[style]} data-testid='specials-category-card'>
        <CompactImageWrapper>
          <CompactImage
            src={image}
            disableSrcSet
            imgixParams={{ width: 224 }}
            htmlAttributes={{ alt: `${label} category image` }}
          />
        </CompactImageWrapper>
        <CompactLabel>{label}</CompactLabel>
      </CompactLink>
    );
  }

  return (
    <CardLink href={href} $radius={cardRadiusOptions[style]} data-testid='specials-category-card'>
      <Label>{label}</Label>
      <ImageContainer>
        <StyledImage
          src={image}
          disableSrcSet
          imgixParams={{ height: 224 }}
          htmlAttributes={{ alt: `${label} category image` }}
        />
      </ImageContainer>
    </CardLink>
  );
}

const CardLink = styled(Link)<{ $radius: number | string }>`
  background: ${({ theme }) => theme.colors?.white ?? 'white'};
  border: 1px solid ${({ theme }) => theme.colors?.greyBorder ?? '#e3e7e9'};
  border-radius: ${({ $radius }) => $radius};
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 16px;
  text-decoration: none;
  cursor: pointer;
  overflow: hidden;
  width: 151px;
  transition: box-shadow 0.15s;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  @media (min-width: 960px) {
    gap: 28px;
    padding: 20px;
    width: 184px;
  }
`;

const TextPillLink = styled(Link)<{ $radius: number | string }>`
  background: ${({ theme }) => theme.colors?.white ?? 'white'};
  border: 2px solid ${({ theme }) => theme.colors?.greyBorder ?? '#e3e7e9'};
  border-radius: ${({ $radius }) => $radius};
  color: ${({ theme }) => theme.colors?.primaryBlack ?? '#121516'};
  display: block;
  font-size: 16px;
  font-weight: 600;
  line-height: ${24 / 16};
  padding: 12px 20px;
  text-align: center;
  text-decoration: none;
  white-space: nowrap;
  transition: border 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.colors?.primaryBlack ?? '#121516'};
  }
`;

// Mirrors categories-nav/cards/stacked-card.tsx: circular/rounded avatar, no outer card chrome
const StackedImageWrapper = styled.div<{ $radius: number | string }>`
  align-items: center;
  background: var(--dcm-categories-card-bg);
  border: 1px solid var(--dcm-categories-card-border-color);
  border-radius: ${({ $radius }) => $radius};
  display: flex;
  height: 112px;
  justify-content: center;
  margin: 0 auto 8px;
  overflow: hidden;
  transition: box-shadow 0.1s;
  width: 112px;

  @media screen and (min-width: 600px) {
    height: 128px;
    width: 128px;
  }
`;

const StackedLink = styled(Link)`
  display: block;
  font-size: 18px;
  font-weight: 600;
  line-height: ${22 / 18};
  text-align: center;
  text-decoration: none;
  white-space: nowrap;

  &:hover ${StackedImageWrapper} {
    box-shadow: var(--dcm-categories-card-hover-shadow);
  }
`;

const StackedImage = styled(Imgix)`
  display: block;
  width: 100%;
`;

const StackedLabel = styled.h3`
  margin: 0;
`;

// Mirrors categories-nav/cards/compact-card.tsx: bordered card, small image top, label below
const CompactLink = styled(Link)<{ $radius: number | string }>`
  background: var(--dcm-categories-card-bg);
  border: 1px solid var(--dcm-categories-card-border-color);
  border-radius: ${({ $radius }) => $radius};
  color: var(--dcm-categories-card-text-color);
  display: block;
  font-size: 16px;
  font-weight: 600;
  line-height: ${24 / 16};
  padding: 12px;
  text-align: center;
  text-decoration: none;
  transition: box-shadow 0.1s;
  white-space: nowrap;
  width: 156px;

  @media screen and (min-width: 600px) {
    line-height: ${22 / 18};
    padding: 16px;
  }

  &:hover {
    box-shadow: var(--dcm-categories-card-hover-shadow);
  }
`;

const CompactImageWrapper = styled.div`
  align-items: center;
  display: flex;
  height: 51px;
  justify-content: center;
  margin: 0 auto 8px;
  overflow: hidden;
  width: 96px;

  @media screen and (min-width: 600px) {
    height: 60px;
    margin-bottom: 12px;
    width: 112px;
  }
`;

const CompactImage = styled(Imgix)`
  display: block;
  max-height: 100%;
`;

const CompactLabel = styled.h3`
  margin: 0;
`;

const ImageContainer = styled.div`
  aspect-ratio: 640 / 340;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledImage = styled(Imgix)`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Label = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors?.primaryBlack ?? '#121516'};
  line-height: 20px;
  width: 100%;

  @media (min-width: 960px) {
    font-size: 20px;
    line-height: 24px;
  }
`;
